import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button, Icon, Overlay, Typography } from '@/components';
import { PriceTrendChartCanvas } from './price-trend-chart';
import { PriceTrendTable } from './price-trend-table';
import { useSidebar } from '@/components/ui/sidebar';

export function PriceTrendCanva({ data, hidden }: any) {
  const { state } = useSidebar();
  const { t } = useTranslation(['insights']);
  const [view, setView] = useState<'chart' | 'table'>('chart');

  const isCollapsed = state === 'collapsed';
  const chartData = data.prices;
  const sanitizedDescriptionComments = DOMPurify.sanitize(data.descriptionComments);

  return (
    <>
      {hidden ? (
        <Overlay />
      ) : (
        <div className="w-full min-w-0 space-y-6 p-2 sm:p-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="text-lg font-semibold sm:text-xl">{data.chartTitle}</CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" color="neutral" disabled={view === 'chart'} onClick={() => setView('chart')}>
                    <Icon name="bar-chart3" className="mr-1 h-4 w-4" />
                    {t('Chart')}
                  </Button>
                  <Button size="sm" color="neutral" disabled={view === 'table'} onClick={() => setView('table')}>
                    <Icon name="table" className="mr-1 h-4 w-4" />
                    {t('Table')}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="min-w-0 overflow-x-auto">
              <div className={isCollapsed ? 'min-w-125' : 'min-w-125 md:min-w-100'}>
                {view === 'chart' ? (
                  <PriceTrendChartCanvas
                    data={chartData}
                    interval={data.interval}
                    minYAxis={data.minYAxis}
                    maxYAxis={data.maxYAxis}
                  />
                ) : (
                  <PriceTrendTable data={chartData} />
                )}
              </div>
            </CardContent>
            <p className="flex items-center gap-1 p-2 text-xs text-gray-400 lg:hidden">
              {`<-> Scroll to see all ${view === 'chart' ? 'chart' : 'table'}`}
            </p>
          </Card>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card className="gap-3 shadow-xs">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <Icon name="map" className="h-7 w-7 rounded-full bg-neutral-50 p-1" />
                  <Typography as="p" weight="semibold" size="title">
                    {t('Legend')}
                  </Typography>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <div className="h-0.5 w-7 bg-green-700"></div>
                  <Typography as="span" weight="normal" size="body-3">
                    {t('Max price')}
                  </Typography>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-7 border-t-2 border-dashed border-orange-500"></div>
                  <Typography as="span" weight="normal" size="body-3">
                    {t('Average price')}
                  </Typography>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-0.5 w-7 bg-gray-800"></div>
                  <Typography as="span" weight="normal" size="body-3">
                    {t('Min price')}
                  </Typography>
                </div>
              </CardContent>
            </Card>

            <Card className="gap-3 shadow-xs">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <Icon name="trending-up-down" className="h-7 w-7 rounded-full bg-neutral-50 p-1" />
                  <Typography as="p" weight="semibold" size="title">
                    {t('Price Trend')}
                  </Typography>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Typography as="p" weight="normal" size="body-3">
                  {data.priceTrend}
                </Typography>
              </CardContent>
            </Card>

            <Card className="gap-3 shadow-xs">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <Icon name="message-square-more" className="h-7 w-7 rounded-full bg-neutral-50 p-1" />
                  <Typography as="p" weight="semibold" size="title">
                    {t('Interest Trend')}
                  </Typography>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Typography as="p" weight="normal" size="body-3">
                  {data.interestTrend}
                </Typography>
              </CardContent>
            </Card>

            <Card className="gap-3 shadow-xs">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <Icon name="search-text" className="h-7 w-7 rounded-full bg-neutral-50 p-1" />
                  <Typography as="p" weight="semibold" size="title">
                    {t('Data Source')}
                  </Typography>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Typography as="p" weight="normal" size="body-3">
                  {data.dataSource}
                </Typography>
              </CardContent>
            </Card>
          </div>

          <Card className="gap-3 shadow-xs">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <Icon name="sticky-note" className="h-7 w-7 rounded-full bg-neutral-50 p-1" />
                <Typography as="p" weight="semibold" size="title">
                  {t('Description and Comments')}
                </Typography>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className="prose max-w-none text-base leading-relaxed font-normal text-neutral-900"
                dangerouslySetInnerHTML={{ __html: sanitizedDescriptionComments }}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
